import { useCallback, useEffect, useRef, useState } from 'react';
import Header from '../Header/Header.jsx';
import Footer from '../Footer/Footer.jsx';
import styles from './DaveJenkinsCaseStudy-Compact.module.css';
import heroImg from '../../images/DJ-hero.png';
import Img3 from '../../images/dj-3.png';
import Img5 from '../../images/dj-5.png';
import Img8 from '../../images/dj-8.png';
import Img16 from '../../images/dj-16.png';

const IMAGE_BASE = '/images/dave-jenkins';

const samples = [
  {
    src: heroImg,
    title: 'Campaign cover',
    label: 'Opening visual',
    wide: true,
  },
  {
    src: Img3,
    title: 'Reach and visibility',
    label: 'Data presentation',
  },
  {
    src: Img5,
    title: 'Racing heritage',
    label: 'Timeline layout',
  },
  {
    src: Img8,
    title: 'Partnership structure',
    label: 'Package comparison',
  },
  {
    src: Img16,
    title: 'Partnership call to action',
    label: 'Closing slide',
  },
];

const projectInfo = [
  ['Project', 'Motorsport sponsorship deck'],
  ['Role', 'Presentation and editorial design'],
  ['Year', '2026'],
  ['Slides', '16'],
];

function ArrowIcon({ direction = 'right' }) {
  return (
    <svg
      viewBox="0 0 24 24"
      aria-hidden="true"
      style={{ transform: direction === 'left' ? 'rotate(180deg)' : undefined }}
    >
      <path
        d="M5 12h13M14 7l5 5-5 5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ExpandIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M9 4H4v5M15 4h5v5M9 20H4v-5M15 20h5v-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.7"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="m6 6 12 12M18 6 6 18"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.8"
        strokeLinecap="round"
      />
    </svg>
  );
}

function Reveal({ as: Element = 'div', className = '', delay = 0, children }) {
  const elementRef = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const element = elementRef.current;
    if (!element) return undefined;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setVisible(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.12, rootMargin: '0px 0px -7% 0px' },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  return (
    <Element
      ref={elementRef}
      className={`${styles.reveal} ${visible ? styles.revealVisible : ''} ${className}`}
      style={{ '--delay': `${delay}ms` }}
    >
      {children}
    </Element>
  );
}

function Lightbox({ index, onClose, onNext, onPrevious }) {
  const item = samples[index];
  const dialogRef = useRef(null);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    dialogRef.current?.focus();

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'ArrowRight') onNext();
      if (event.key === 'ArrowLeft') onPrevious();
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [onClose, onNext, onPrevious]);

  return (
    <div
      ref={dialogRef}
      className={styles.lightbox}
      role="dialog"
      aria-modal="true"
      aria-label={`${item.title} preview`}
      tabIndex={-1}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <button className={styles.closeButton} type="button" onClick={onClose} aria-label="Close preview">
        <CloseIcon />
      </button>

      <button className={`${styles.lightboxButton} ${styles.previousButton}`} type="button" onClick={onPrevious} aria-label="Previous image">
        <ArrowIcon direction="left" />
      </button>

      <figure>
        <img src={item.src} alt={`${item.title} presentation screen`} />
        <figcaption>
          <span>{String(index + 1).padStart(2, '0')} / {samples.length}</span>
          <strong>{item.title}</strong>
        </figcaption>
      </figure>

      <button className={`${styles.lightboxButton} ${styles.nextButton}`} type="button" onClick={onNext} aria-label="Next image">
        <ArrowIcon />
      </button>
    </div>
  );
}

export default function DaveJenkinsCaseStudy() {
  const [activeIndex, setActiveIndex] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [mode, setMode] = useState('graphic');
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const closeLightbox = useCallback(() => setActiveIndex(null), []);
  const nextImage = useCallback(() => {
    setActiveIndex((current) => (current === null ? 0 : (current + 1) % samples.length));
  }, []);
  const previousImage = useCallback(() => {
    setActiveIndex((current) => (current === null ? 0 : (current - 1 + samples.length) % samples.length));
  }, []);

  return (
    <>
      <Header mode={mode} setMode={setMode} menuOpen={menuOpen} setMenuOpen={setMenuOpen} scrolled={scrolled} />
      <main id="top" className={styles.page}>
        <header className={styles.hero}>
          <img
            className={styles.heroImage}
            src={heroImg}
            alt="Dave Jenkins motorsport sponsorship presentation"
            fetchPriority="high"
          />
          <div className={styles.heroShade} />
          <div className={styles.heroContent}>
            <p className={styles.kicker}>Presentation design · 2026</p>
            <h1>Dave Jenkins</h1>
            <h2>Motorsport sponsorship deck</h2>
            <p>
              A bold commercial presentation combining racing photography, clear data and structured sponsorship packages.
            </p>
            <a href="#samples" className={styles.viewButton}>
              View selected screens
              <ArrowIcon />
            </a>
          </div>
        </header>

      <section className={styles.introSection}>
        <div className={styles.shell}>
          <Reveal className={styles.introGrid}>
            <div>
              <p className={styles.sectionLabel}>Project summary</p>
              <h2>Fast, focused and commercially clear.</h2>
            </div>
            <p className={styles.summaryCopy}>
              The visual system uses strong typography, diagonal compositions and modular information blocks to keep the presentation energetic and easy to scan.
            </p>
          </Reveal>

          <div className={styles.infoGrid}>
            {projectInfo.map(([label, value], index) => (
              <Reveal className={styles.infoItem} delay={index * 60} key={label}>
                <span>{label}</span>
                <strong>{value}</strong>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section id="samples" className={styles.samplesSection}>
        <div className={styles.shell}>
          <Reveal className={styles.samplesHeading}>
            <div>
              <p className={styles.sectionLabel}>Selected screens</p>
              <h2>A few highlights from the full presentation.</h2>
            </div>
            <p>Click a screen to view it larger.</p>
          </Reveal>

          <div className={styles.samplesGrid}>
            {samples.map((sample, index) => (
              <Reveal
                className={`${styles.sampleCard} ${sample.wide ? styles.sampleCardWide : ''}`}
                delay={(index % 3) * 70}
                key={sample.src}
              >
                <button type="button" onClick={() => setActiveIndex(index)} aria-label={`Open ${sample.title}`}>
                  <img src={sample.src} alt={`${sample.title} screen`} loading="lazy" decoding="async" />
                  <span className={styles.sampleOverlay}>
                    <small>{sample.label}</small>
                    <strong>{sample.title}</strong>
                    <span className={styles.expandIcon}><ExpandIcon /></span>
                  </span>
                </button>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className={styles.closingSection}>
        <div className={styles.shell}>
          <Reveal className={styles.closingContent}>
            <p className={styles.sectionLabel}>Presentation portfolio</p>
            <h2>Designed to feel powerful on track and professional in the boardroom.</h2>
            <a href="https://www.pavithrachandran.com/#contact">
              Start a project
              <ArrowIcon />
            </a>
          </Reveal>
        </div>
      </section>

        {activeIndex !== null && (
          <Lightbox
            index={activeIndex}
            onClose={closeLightbox}
            onNext={nextImage}
            onPrevious={previousImage}
          />
        )}
      </main>
      <Footer mode={mode} />
    </>
  );
}